import { ideaResult } from '@/services/result-service';
import { computed, onMounted, ref } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { reportService } from '@/services/report-service';
import {
    exportDoc,
    getContentFromStorage,
} from '@/infrastructure/api-functions';
import { createIframeDownload } from '@eureka/services/src/flow';
import { map, inRange, size, get } from 'lodash';
import { useGlobalState } from './use-global-store';
import { i18n } from '@/infrastructure/locales';
import { TRANSLATE_LANGUAGES } from '@/types';
import { useI18n } from 'vue-i18n';
import { controlDeferred } from '@eureka/utils/control';
import { Toast } from 'patsnap-biz';
import { injectService } from '@patsnap/tahiti-core/ioc/inject';
import { BiTrackService } from '@eureka/domains/src/bi-track/services/bi-track-service';
import { EurekaTrackEventType } from '@eureka/domains/src/bi-track/types';
import { FunctionId } from '@/types/function-id';

const meansTextRange = [50, 2000];
const efficacyTextRange = [10, 2000];

export function useIdeaInput() {
    const biTrackService = injectService<BiTrackService>(BiTrackService);
    const { country, legal, updateInputLang, editContent, updateEditContent } =
        useGlobalState();

    /**
     * 下载权限
     */
    const downloadPermission = ref(false);

    const exportLoading = ref(false);

    /**
     * 是否重新排查状态
     */
    const reCheckStatus = computed(() => {
        return ideaResult.value.searchCounts > 0;
    });

    const haveResult = computed(() => {
        return ideaResult.value.results.length > 0;
    });

    const isLoadingResult = computed(() => {
        return ideaResult.value.getResultLoading;
    });

    const filterErrorStatus = computed(() => {
        return size(country.value) < 1 || size(legal.value) < 1;
    });

    const efficacyValid = computed(() => {
        if (!editContent.efficacy) return true;
        return inRange(
            editContent.efficacy.length || 0,
            efficacyTextRange[0],
            efficacyTextRange[1] + 1
        );
    });

    const meansValid = computed(() => {
        if (!editContent.means) return true;
        return inRange(
            editContent.means.length || 0,
            meansTextRange[0],
            meansTextRange[1] + 1
        );
    });

    const searchBtnDisable = computed(() => {
        if (!editContent.means) return true;
        return !meansValid.value || !efficacyValid.value || filterErrorStatus.value;
    });

    const router = useRouter();
    const route = useRoute();

    function submitCheck() {
        if (searchBtnDisable.value) return;
        if (route.name !== 'results') {
            router.push({ name: 'result' });
        }
        ideaResult.value.results = [];
        updateInputLang(TRANSLATE_LANGUAGES.NONE);
        reportService.value.reportList = [];
        const text = editContent.means;
        ideaResult.value
            .getResults({
                ...editContent,
                country: country.value,
                legal: legal.value,
            })
            .then((res) => {
                biTrackService.biTrack(EurekaTrackEventType.HOME, {
                    sourceType: 'home_create_task',
                    eventDetailStr: {
                        type: FunctionId.NOVELTY_CHECK,
                        text,
                    },
                });
                if (res?.lang) {
                    updateInputLang(res.lang);
                }
            })
    }

    function handleFillExample() {
        updateEditContent({
            efficacy: i18n.global.t('exampleEfficacy'),
            means: i18n.global.t('exampleMethod'),
        });
    }

    function downloadDisclosure() {
        exportLoading.value = true;
        const { means, efficacy } = editContent;
        exportDoc({
            source_efficacy: efficacy,
            source_means: means,
            pair_patents: map(
                ideaResult.value.results,
                ({ patent_id, similarity, pn, title }) => ({
                    patent_id,
                    similarity,
                    pn,
                    title,
                })
            ),
        })
            .then((downloadUrl) => {
                createIframeDownload(downloadUrl);
            })
            .finally(() => {
                exportLoading.value = false;
            });
    }

    const { t } = useI18n();

    /**
     * 获取有可能从AI交底书模块传递的输入内容
     */
    async function getContentFromAIDisclosure() {
        if (!get(route.query, 'id')) return;
        const { means, efficacy, means_overrun, efficacy_overrun } =
            await getContentFromStorage(get(route.query, 'id') as string);
        updateEditContent({
            efficacy,
            means,
        });
        if (means_overrun || efficacy_overrun) {
            Toast({
                message: t('gpt.inputOverRun', { number: 2000 }),
                type: 'warning',
            });
        }
    }

    onMounted(async() => {
        getContentFromAIDisclosure();
        /**
          * 用户是否可以下载报告模版
         */
        const { download } = await controlDeferred.promise;
        downloadPermission.value = download;
    });

    return {
        searchBtnDisable,
        haveResult,
        reCheckStatus,
        submitCheck,
        handleFillExample,
        downloadDisclosure,
        downloadPermission,
        exportLoading,
        isLoadingResult,
        efficacyValid,
        meansValid,
        filterErrorStatus,
    };
}
